import { useMutation } from '@apollo/client';
import { ResolveBlock } from '@bechtel/client-blocks';
import { checkIfUnitOfMeasureIsHours, SuspenseLoader, useAppUser, useBlockPermission, useConfig, useWorkPackageOwner } from '@bechtel/client-common';
import { CLAIM_MILESTONE, UNCLAIM_MILESTONE } from '@bechtel/client-gql';
import { Box, Icon, IconSize, Spinner, Text, useModalState, useSideSheetState } from '@bechtel/gds';
import {
  CommodityStatusCode,
  ItrMilestoneAction,
  ItrType,
  MilestoneStatusCode
} from '@bechtel/types';
import { t } from '@lingui/macro';
import { addDays, isBefore, parseISO } from 'date-fns';
import { every, find, get, map, some } from 'lodash';
import { FC, Fragment, Suspense } from 'react';
import { ConfirmClaimAction } from '~/components/molecules/confirm';
import ReclaimMilestone from '~/detail/commodities/milestones/reclaim/internals';
import RejectMilestone from '~/detail/commodities/milestones/reject/internals';
import { useWorkPackage } from '~/detail/context';
import { useWorkPackagePermissions } from '~/hooks/use-work-package-permissions';
import { clearCacheByKey, QueryKeys } from '~/services/cache';
import { updateWorkPackageInCache } from '~/services/work-package-cache-utils';
import { WeldoutMilestoneAssign } from '../assign';
import { MilestoneError } from '../error';
import { useApproveMilestone } from '../hooks/use-approve-milestone';
import { useUnapproveMilestone } from '../hooks/use-unapprove-milestone';
import { MilestoneStatusExtended } from '../types';
import WeldFlangeConnections from '../weld-flange-connections';
import { MilestoneActionButtonStyled, SecondaryActionButtonStyled } from './styled';
import { MilestoneActionContentProps, MilestoneActionProps, MilestoneNonActionProps } from './types';

export const ActionSpinner = ({ label }) => {
  return (
    <Fragment>
      <Box mr={{ sm: '400' }}>
        <Spinner></Spinner>
      </Box>
      <Text color="grey.1000" size="300">
        {label}
      </Text>
    </Fragment>
  );
};

export const ActionContent: FC<MilestoneActionContentProps> = ({ icon, label }) => {
  return (
    <Fragment>
      <Box mr={{ sm: '400' }}>
        <Icon icon={icon} size={IconSize.MEDIUM} />
      </Box>
      <Text color="grey.1000" size="300">
        {label}
      </Text>
    </Fragment>
  );
};

export const UnclaimAction: FC<MilestoneActionProps> = ({
  milestone,
  status,
  disabled,
  refetchQueries = [],
  customStyle,
  onActionUpdating,
  onActionCompleted,
}) => {
  const [unClaimMilestone, { loading, data, error }] = useMutation(UNCLAIM_MILESTONE as any, {
    variables: {
      input: {
        commodityMilestoneId: milestone.id,
      },
    },
    update: (cache, result) => {
      if (result.data && result.data.unclaimMilestone.commodity) {
        clearCacheByKey(cache, QueryKeys.EARNABLE_HOURS);

        // Update work package cache with new percentage complete if available
        const workPackageData = result.data.unclaimMilestone.workPackage;
        if (workPackageData?.id) {
          const updates: { percentageComplete?: number | null; earnedHours?: number | null } = {};
          if (workPackageData.percentageComplete !== undefined) {
            updates.percentageComplete = workPackageData.percentageComplete;
          }
          if (workPackageData.earnedHours !== undefined) {
            updates.earnedHours = workPackageData.earnedHours;
          }

          if (Object.keys(updates).length > 0) {
            updateWorkPackageInCache({
              cache,
              workPackageId: workPackageData.id,
              updates
            });
          }
        }
      }
    },
    onCompleted: () => {
      if (onActionCompleted) onActionCompleted();
    },
    onError: () => {
      if (onActionCompleted) onActionCompleted();
    },
    // Remove refetchQueries to prevent list reload
    // refetchQueries: refetchQueries,
  });
  const milestoneError = get(data, 'unclaimMilestone.error') || error;
  return (
    <>
      {milestoneError && <MilestoneError milestoneError={milestoneError}></MilestoneError>}
      <MilestoneActionButtonStyled
        status={status}
        onClick={() => {
          if (onActionUpdating) onActionUpdating();
          unClaimMilestone();
        }}
        disabled={disabled || loading}
        data-testid="milestone-unclaim-button"
        style={customStyle}
      >
        {loading && <ActionSpinner label={t`Unclaim`}></ActionSpinner>}
        {!loading && <ActionContent label={t`Unclaim`} icon={disabled ? 'lock' : 'minus'} />}
      </MilestoneActionButtonStyled>
    </>
  );
};

export const ClaimAction: FC<MilestoneActionProps> = ({
  milestone,
  status,
  disabled,
  onActionUpdating,
  onActionCompleted,
  refetchQueries = [],
}) => {
  const itrRequired = some(milestone.type.projectItrConfig, (item) => {
    return item.itrType === ItrType.PIPE_CLEANLINESS && item?.itrMilestoneAction === ItrMilestoneAction.CLAIM;
  });

  //check if the connected welds(if there are any have already asked for ITR certificate)
  const isConnectedWeldsAlreadyAskedForCert =
    milestone.weldStatuses &&
    milestone.weldStatuses?.length > 0 &&
    some(milestone.weldStatuses, (weld) => weld?.itrRequired);

  const { visible, open, close } = useModalState(false);
  const [claimMilestone, { loading, data, error }] = useMutation(CLAIM_MILESTONE as any, {
    variables: {
      input: {
        commodityMilestoneId: milestone.id,
        itrRequired: itrRequired && !isConnectedWeldsAlreadyAskedForCert,
      },
    },
    update: (cache, result) => {
      if (result.data && result.data.claimMilestone) {
        clearCacheByKey(cache, QueryKeys.EARNABLE_HOURS);

        // Update work package cache with new percentage complete if available
        const workPackageData = result.data.claimMilestone.workPackage;
        if (workPackageData?.id) {
          const updates: { percentageComplete?: number | null; earnedHours?: number | null } = {};
          if (workPackageData.percentageComplete !== undefined) {
            updates.percentageComplete = workPackageData.percentageComplete;
          }
          if (workPackageData.earnedHours !== undefined) {
            updates.earnedHours = workPackageData.earnedHours;
          }

          if (Object.keys(updates).length > 0) {
            updateWorkPackageInCache({
              cache,
              workPackageId: workPackageData.id,
              updates
            });
          }
        }
      }
    },
    onCompleted: () => {
      if (itrRequired) close();
      if (onActionCompleted) onActionCompleted();
    },
    onError: () => {
      if (onActionCompleted) onActionCompleted();
    },
    // Remove refetchQueries to prevent list reload
    // refetchQueries: refetchQueries,
  });
  const milestoneError = get(data, 'claimMilestone.error') || error;

  const onCertifyClick = () => {
    if (onActionUpdating) onActionUpdating();
    claimMilestone();
  };

  const onClaim = () => {
    if (itrRequired && !isConnectedWeldsAlreadyAskedForCert) open();
    else {
      if (onActionUpdating) onActionUpdating();
      claimMilestone();
    }
  };

  return (
    <>
      {visible && !milestoneError && (
        <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
          <ConfirmClaimAction onCertifyClick={onCertifyClick} isMutationInProgress={loading} close={close} />
        </Suspense>
      )}
      {milestoneError && <MilestoneError milestoneError={milestoneError}></MilestoneError>}
      <MilestoneActionButtonStyled
        status={status}
        onClick={() => onClaim()}
        disabled={disabled || (loading && !(itrRequired && !isConnectedWeldsAlreadyAskedForCert))}
        data-testid="milestone-claim-action"
      >
        {loading && !(itrRequired && !isConnectedWeldsAlreadyAskedForCert) && (
          <ActionSpinner label={t`Claim`}></ActionSpinner>
        )}
        {(!loading || (itrRequired && !isConnectedWeldsAlreadyAskedForCert)) && (
          <ActionContent label={t`Claim`} icon="plus" />
        )}
      </MilestoneActionButtonStyled>
    </>
  );
};

export const ReclaimAction: FC<MilestoneActionProps> = ({
  milestone,
  status,
  disabled,
  onActionCompleted,
  refetchQueries = [],
}) => {
  const { visible, open, close } = useSideSheetState(false);
  return (
    <Fragment>
      <MilestoneActionButtonStyled
        disabled={disabled}
        status={status}
        onClick={open}
        data-testid="milestone-reclaim-action"
      >
        <ActionContent label={t`Reclaim`} icon="undo" />
      </MilestoneActionButtonStyled>
      <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
        {visible && (
          <ReclaimMilestone
            onClose={close}
            milestone={milestone}
            refetchQueries={refetchQueries}
            onActionCompleted={onActionCompleted}
          ></ReclaimMilestone>
        )}
      </Suspense>
    </Fragment>
  );
};

export const ResolveBlockAction: FC<MilestoneActionProps> = ({ status, openBlockSidesheet }) => {
  return (
    <Fragment>
      <MilestoneActionButtonStyled status={status} onClick={openBlockSidesheet} data-testid="resolve-block-action">
        <ActionContent label={t`Resolve block`} icon="exclamation-triangle" />
      </MilestoneActionButtonStyled>
    </Fragment>
  );
};

export const WeldAssignAction: FC<MilestoneActionProps> = ({ milestone, status, actionText, disabled }) => {
  const { user } = useAppUser();
  const isUnitOfMeasureHour = checkIfUnitOfMeasureIsHours(user);
  const { visible, open, close } = useSideSheetState(false);
  const { features } = useConfig();
  const isFitupEnabled = features.canApproveFitupInspection ? features.canApproveFitupInspection : false;
  const weldIds = map(milestone.weldStatuses, 'id');
  const itrRequired = some(milestone.type.projectItrConfig, (item) => {
    return item.itrType === ItrType.PIPE_CLEANLINESS && item?.itrMilestoneAction === ItrMilestoneAction.CLAIM;
  });

  return (
    <Fragment>
      <MilestoneActionButtonStyled
        disabled={disabled}
        status={status}
        onClick={open}
        data-testid="milestone-weld-assign-action"
      >
        <ActionContent label={actionText || t`Assign/Unassign`} icon="user-plus" />
      </MilestoneActionButtonStyled>
      {visible && (
        <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
          {isFitupEnabled ? (
            <WeldFlangeConnections
              weldIds={weldIds}
              close={close}
              itrRequired={itrRequired}
              isUnitOfMeasureHour={isUnitOfMeasureHour}
            />
          ) : (
            <WeldoutMilestoneAssign milestone={milestone} onClose={close} />
          )}
        </Suspense>
      )}
    </Fragment>
  );
};

const NonAction: FC<MilestoneNonActionProps> = ({ icon, label }) => {
  return (
    <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} p={{ sm: '400' }} flexGrow={{ sm: 1 }}>
      <Box mr={{ sm: '400' }}>
        <Icon icon={icon} />
      </Box>
      <Text size="300" color="grey.1000">
        {label}
      </Text>
    </Box>
  );
};

export const RejectAction: FC<MilestoneActionProps> = ({
  milestone,
  refetchQueries = [],
  customStyle,
  disabled,
  onActionCompleted,
}) => {
  const { visible, open, close } = useSideSheetState(false);
  return (
    <Fragment>
      <SecondaryActionButtonStyled
        onClick={open}
        data-testid="milestone-reject-action"
        style={customStyle}
        disabled={disabled}
      >
        <ActionContent label={t`Reject`} icon="times" />
      </SecondaryActionButtonStyled>
      <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
        {visible && (
          <RejectMilestone
            onClose={close}
            milestone={milestone}
            refetchQueries={refetchQueries}
            onActionCompleted={onActionCompleted}
          ></RejectMilestone>
        )}
      </Suspense>
    </Fragment>
  );
};

export const ApproveAction: FC<MilestoneActionProps> = ({
  milestone,
  status,
  refetchQueries = [],
  customStyle,
  onActionUpdating,
  onActionCompleted,
  disabled,
}) => {
  const { visible, open, close } = useModalState(false);
  const [approveMilestone, { loading: approving, data, error }] = useApproveMilestone(onActionCompleted);
  const milestoneError = get(data, 'approveMilestone.error') || error;
  const itrRequired = some(milestone.type.projectItrConfig, (item) => {
    return item.itrType === ItrType.PIPE_CLEANLINESS && item?.itrMilestoneAction === ItrMilestoneAction.APPROVE;
  });

  const onCertifyClick = () => {
    if (onActionUpdating) onActionUpdating();
    approveMilestone({
      variables: {
        input: {
          commodityMilestoneId: milestone.id,
          itrRequired: itrRequired,
        },
      },
      refetchQueries: refetchQueries,
    });
  };
  return (
    <Fragment>
      {visible && !milestoneError && (
        <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
          <ConfirmClaimAction onCertifyClick={onCertifyClick} isMutationInProgress={approving} close={close} />
        </Suspense>
      )}
      {milestoneError && <MilestoneError milestoneError={milestoneError} />}
      <MilestoneActionButtonStyled
        status={status}
        onClick={() => {
          if (itrRequired) {
            open();
          } else {
            if (onActionUpdating) onActionUpdating();
            approveMilestone({
              variables: {
                input: {
                  commodityMilestoneId: milestone.id,
                  itrRequired: itrRequired,
                },
              },
              refetchQueries: refetchQueries,
            });
          }
        }}
        disabled={disabled || approving}
        data-testid="milestone-approve-action"
        style={customStyle}
      >
        {approving && <ActionSpinner label={t`Approve`}></ActionSpinner>}
        {!approving && <ActionContent label={t`Approve`} icon="check" />}
      </MilestoneActionButtonStyled>
    </Fragment>
  );
};
const ApproveRejectAction: FC<MilestoneActionProps> = ({ milestone, status }) => {
  const { assignedCrewMembers } = useWorkPackage();
  const { canApproveMilestone, canRejectMilestone } = useWorkPackagePermissions(assignedCrewMembers);

  return (
    <Fragment>
      {canRejectMilestone && <RejectAction milestone={milestone} status={status}></RejectAction>}
      {canApproveMilestone && <ApproveAction milestone={milestone} status={status}></ApproveAction>}
    </Fragment>
  );
};
const ApproveUnclaimAction: FC<MilestoneActionProps> = ({ status, milestone, disabled }) => {
  const { assignedCrewMembers } = useWorkPackage();
  const { canApproveMilestone, canClaimMilestone } = useWorkPackagePermissions(assignedCrewMembers);
  return (
    <Fragment>
      {canClaimMilestone && (
        <>
          <UnclaimAction disabled={disabled} milestone={milestone} status={status}></UnclaimAction>
          &nbsp;&nbsp;
        </>
      )}
      {canApproveMilestone && <ApproveAction milestone={milestone} status={status}></ApproveAction>}
    </Fragment>
  );
};
export const UnapproveAction: FC<MilestoneActionProps> = ({
  milestone,
  status,
  refetchQueries = [],
  customStyle,
  onActionUpdating,
  onActionCompleted,
  disabled,
}) => {
  const [unapproveMilestone, { loading, data, error }] = useUnapproveMilestone(onActionCompleted);
  const milestoneError = get(data, 'unapproveMilestone.error') || error;
  return (
    <>
      {milestoneError && <MilestoneError milestoneError={milestoneError}></MilestoneError>}
      <MilestoneActionButtonStyled
        status={status}
        onClick={() => {
          if (onActionUpdating) onActionUpdating();
          unapproveMilestone({
            variables: {
              input: {
                commodityMilestoneId: milestone.id,
              },
            },
            refetchQueries: refetchQueries,
          });
        }}
        disabled={disabled || loading}
        style={customStyle}
        data-testid="milestone-unapprove-action"
      >
        {loading && <ActionSpinner label={t`Unapprove`}></ActionSpinner>}
        {!loading && <ActionContent label={t`Unapprove`} icon="minus" />}
      </MilestoneActionButtonStyled>
    </>
  );
};

const Action: FC<MilestoneActionProps> = (props) => {
  const { features } = useConfig();
  const { assignedCrewMembers, facilities, primaryOwners, secondaryOwners, otherOwners } = useWorkPackage();
  const { user } = useAppUser();
  const isOwner = useWorkPackageOwner(assignedCrewMembers);
  const { canClaimMilestone, canApproveMilestone, canRejectMilestone, canEditWeldForeman } =
    useWorkPackagePermissions(assignedCrewMembers);
  const { canResolveBlock } = useBlockPermission(assignedCrewMembers, facilities ? facilities : []);
  const { milestone, status, openBlockSidesheet, isUserClaimer } = props;
  const isFitupEnabled = features.canApproveFitupInspection ? features.canApproveFitupInspection : false;
  const hasWeld = milestone.weldStatuses && milestone.weldStatuses.length > 0;
  //TODO: Uncomment use and createdBy after IAM integration. This helps to test unclaim functionality
  const createdByUserId = get(milestone, 'currentStatus.createdBy.id');
  const createdAt = get(milestone, 'currentStatus.createdAt');
  const parsedCreatedAt = createdAt ? parseISO(createdAt) : undefined;
  const currentProjectWeek = find(user?.project?.projectConfiguration?.projectWeeks, (projectWeek) =>
    projectWeek?.isCurrent ? true : false
  );
  const currentWeekStartDate = parseISO(currentProjectWeek?.startDate); //parseIso is required since weekStartDate is date(NOT datetime) string
  const isClaimer = status === MilestoneStatusCode.CLAIMED && isUserClaimer;

  // Check if secondary/other owners should be blocked from unclaiming primary owner's milestone
  const workPackagePrimaryOwners = primaryOwners || [];
  const workPackageSecondaryOwners = secondaryOwners || [];
  const workPackageOtherOwners = otherOwners || [];
  const isClaimerPrimaryOwner = some(workPackagePrimaryOwners, (owner) => owner?.crewMember?.id === createdByUserId);
  const isCurrentUserSecondaryOwner = some(workPackageSecondaryOwners, (owner) => owner?.crewMember?.id === user?.id);
  const isCurrentUserOtherOwner = some(workPackageOtherOwners, (owner) => owner?.crewMember?.id === user?.id);
  const shouldBlockSecondaryOwnerUnclaim =
    isClaimerPrimaryOwner && (isCurrentUserSecondaryOwner || isCurrentUserOtherOwner);

  const getClaimActionContent = () => {
    if (canClaimMilestone || canEditWeldForeman) {
      if (hasWeld) {
        const hasAllApprovedWeld = every(
          milestone.weldStatuses,
          (weld) => weld?.status?.status === CommodityStatusCode.VT_APPROVED
        );
        return hasAllApprovedWeld ? (
          <ClaimAction {...props} />
        ) : canEditWeldForeman && !isFitupEnabled ? (
          <WeldAssignAction {...props} />
        ) : null;
      }
      return <ClaimAction {...props} />;
    }
    return null;
  };
  const unclaimProps = {
    ...props,
    disabled:
      (isOwner && createdAt && isBefore(new Date(createdAt), new Date(addDays(new Date(), -2).toISOString()))) ||
      shouldBlockSecondaryOwnerUnclaim,
  };
  switch (status) {
    case MilestoneStatusCode.PLANNED:
      if (canClaimMilestone) return getClaimActionContent();
      return null;
    case MilestoneStatusCode.CLAIMED:
      if (isClaimer && !canApproveMilestone) {
        // CLAIMED -> UNCLAIM
        // claimed by user and hence can always unclaim!
        if (canClaimMilestone) return <UnclaimAction {...unclaimProps} />;
      } else if (!isClaimer && (canApproveMilestone || canRejectMilestone)) {
        // CLAIMED -> APPROVE / REJECT
        // Other user claimed, user gets option to Reject / Approve
        return <ApproveRejectAction {...props} />;
      } else if (isClaimer && (canApproveMilestone || canClaimMilestone)) {
        // CLAIMED -> APPROVE / UNCLAIM
        // FE claimed, they get option to Unclaim / Approve
        return <ApproveUnclaimAction {...unclaimProps} />;
      } else if (isOwner) {
        return <UnclaimAction {...unclaimProps} />;
      }
      return <NonAction icon="check" label={t`Claimed`} />;
    case MilestoneStatusCode.APPROVED:
      // APPROVED -> UNAPPROVE
      if (
        canApproveMilestone &&
        user?.id === createdByUserId &&
        parsedCreatedAt &&
        !isBefore(parsedCreatedAt, currentWeekStartDate)
      ) {
        return <UnapproveAction {...props} />;
      }
      // NO ACTION: display only
      return <NonAction icon="check" label={t`Approved`} />; // display only
    case MilestoneStatusCode.REJECTED:
      // REJECTED -> CLAIM
      if (canClaimMilestone) return <ReclaimAction {...props} />;
      // NO ACTION: display only
      return <NonAction icon="times" label={t`Rejected`} />; // display only
    case MilestoneStatusExtended.BLOCKED:
      if (canResolveBlock) {
        // BLOCKED -> RESOLVE
        return <ResolveBlockAction {...props} openBlockSidesheet={openBlockSidesheet} />;
      }
      return <NonAction icon="times" label={t`Blocked`} />; // display only
    default:
      return getClaimActionContent();
  }
};

export const MilestoneAction: FC<MilestoneActionProps> = (props) => {
  const { id, tag, facilities, assignedCrewMembers } = useWorkPackage();
  // NOTE: Approved status only has an action tied to if it is a FE persona
  // TODO: Account for FE action once we have personas wired up
  const { milestone } = props;
  const { visible, open, close } = useSideSheetState(false);
  const commodity = milestone.commodity?.materialCommodity || milestone.commodity?.weld;
  return (
    <>
      <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
        {visible && (
          <ResolveBlock
            workPackageId={id}
            workPackageTag={tag}
            facilities={facilities}
            assignedCrewMembers={assignedCrewMembers}
            commodityTag={commodity?.tag}
            commodityTypeName={commodity?.type.name}
            milestone={milestone}
            onClose={close}
          />
        )}
      </Suspense>
      <Action {...props} openBlockSidesheet={open}></Action>
    </>
  );
};
